'use client';

import Link from 'next/link';
import AnimateIn from '@/components/ui/AnimateIn';
import AppImage from '@/components/ui/AppImage';
import { ScrollTracker } from '@/components/ui/ScrollTracker';
import { useProductContext } from '@/contexts/ProductContext';
import { getBlurDataURL } from '@/lib/shimmer';

const ProductCard = ({ product, isLarge = false, center = false }) => {
    const headingId = `product-${(product.href ?? product.id ?? product.name)
        .toString()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '')}-title`;
    return (
        <li
            className={`flex flex-col items-center justify-start flex-wrap relative overflow-hidden bg-transparent min-h-48 ${isLarge ? 'h-[400px]' : 'h-[350px]'} group list-none`}
        >
            <Link
                href={product.href}
                aria-labelledby={headingId}
                className="w-full h-full focus:outline-none focus-visible:ring-4 focus-visible:ring-brand "
            >
                <div
                    className={`absolute inset-0 flex flex-col items-center justify-start pt-1 px-4 pb-4 ${
                        !isLarge && 'h-full'
                    }`}
                >
                    <div
                        className={`w-full h-[95%] flex justify-center items-end ${
                            isLarge ? 'py-6' : 'py-2'
                        }`}
                    >
                        <div
                            className={`absolute h-1/2 z-1 rounded-xl bg-gray-100 ${
                                isLarge ? 'w-4/5' : 'w-[85%]'
                            }`}
                            style={{ bottom: '15%' }}
                        />
                        <AppImage
                            src={product.img}
                            alt={product.name}
                            // Provide explicit dimensions - product cards display at ~250-300px
                            width={300}
                            height={180}
                            // Accurate sizes: cards are ~250px on desktop, 60vw on mobile
                            sizes="(max-width: 640px) 60vw, (max-width: 1024px) 30vw, 280px"
                            style={{
                                position: 'relative',
                                bottom: center ? '25%' : undefined,
                                width: isLarge ? '70%' : '80%',
                                maxHeight: isLarge ? '80%' : '95%',
                                objectFit: 'contain',
                                zIndex: 10,
                            }}
                            className={`transition-transform duration-300 ${center ? '' : 'mb-6'}`}
                            loading="lazy"
                            placeholder="blur"
                            blurDataURL={getBlurDataURL(16, 10)}
                        />
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 px-4 transition-opacity duration-300 group-hover:opacity-75">
                    <h3
                        id={headingId}
                        className={`text-black font-semibold ${isLarge ? 'text-xl' : 'text-lg'} wrap-break-word`}
                    >
                        {product.name}
                    </h3>
                    <p
                        className={`text-brand-dark ${
                            isLarge ? 'text-md' : 'text-sm'
                        } underline-offset-4 hover:underline`}
                    >
                        View Details
                    </p>
                </div>
            </Link>
        </li>
    );
};

const Products = () => {
    const { products } = useProductContext();

    // Filter products for each row based on product names
    const firstRowProducts = products.filter(
        (product) =>
            product.name.toLowerCase().includes('trench shield') ||
            product.name.toLowerCase().includes('shield box'),
    );

    const secondRowProducts = products.filter(
        (product) =>
            product.name.toLowerCase().includes('versa') ||
            product.name.toLowerCase().includes('octo box'),
    );

    const thirdRowProducts = products.filter(
        (product) =>
            product.name.toLowerCase().includes('litebox') ||
            product.name.toLowerCase().includes('supa shield') ||
            product.name.toLowerCase().includes('siltmate') ||
            product.name.toLowerCase().includes('accessories') ||
            product.name.toLowerCase().includes('gn solids control'),
    );

    return (
        <section
            className="relative"
            style={{
                minHeight:
                    'calc(100vh - 80px - var(--announcement-offset, 0px))',
            }}
        >
            <ScrollTracker sectionName="featured_products" threshold={0.3} />
            <div className="absolute inset-0 w-full h-full bg-white z-3" />
            <div
                className="absolute inset-0 w-full h-full opacity-10 z-2"
                style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, black 1px, transparent 0)`,
                    backgroundSize: '20px 20px',
                }}
            />
            <div className="w-full h-full flex flex-col items-center justify-center z-20 py-10">
                <div className="w-full max-w-7xl mx-auto z-20 px-4 sm:px-6 lg:px-8">
                    <AnimateIn>
                        <div className="text-center mb-4 mt-4 z-20">
                            <h3 className="text-3xl font-bold text-black z-20">
                                Trench Shield Range
                            </h3>
                            <div className="w-24 h-1 bg-brand mx-auto mt-2" />
                        </div>
                    </AnimateIn>
                    {/* First row: Trench Shields and Trench Shield Boxes */}
                    <AnimateIn delay={0.1}>
                        <ul
                            className="grid grid-cols-1 md:grid-cols-2 list-none"
                            aria-label="Trench Shield products"
                        >
                            {firstRowProducts.map((product, index) => (
                                <ProductCard
                                    key={
                                        product.href ??
                                        product.id ??
                                        product.name
                                            .replace(/\s+/g, '-')
                                            .replace(/[^\w-]/g, '')
                                    }
                                    product={product}
                                    isLarge={true}
                                    variant="pattern"
                                />
                            ))}
                        </ul>
                    </AnimateIn>

                    <AnimateIn>
                        <div className="text-center py-12">
                            <h3 className="text-3xl font-bold text-black">
                                Versa Range
                            </h3>
                            <div className="w-24 h-1 bg-brand mx-auto mt-2" />
                        </div>
                    </AnimateIn>
                    {/* Second row: Versa shields, Versa Manhole Shields & Octo Boxes */}
                    <AnimateIn delay={0.1}>
                        <ul
                            className="grid grid-cols-1 md:grid-cols-3 list-none"
                            aria-label="Versa Range products"
                        >
                            {secondRowProducts.map((product) => (
                                <ProductCard
                                    key={
                                        product.href ??
                                        product.id ??
                                        product.name
                                            .replace(/\s+/g, '-')
                                            .replace(/[^\w-]/g, '')
                                    }
                                    product={product}
                                    isLarge={false}
                                    variant="pattern"
                                />
                            ))}
                        </ul>
                    </AnimateIn>

                    <AnimateIn>
                        <div className="text-center py-12">
                            <h3 className="text-3xl font-bold text-black">
                                Other Products
                            </h3>
                            <div className="w-24 h-1 bg-brand mx-auto mt-2" />
                        </div>
                    </AnimateIn>
                    {/* Third row: Litebox, Supa Shield, Siltmate and Accessories */}
                    <AnimateIn delay={0.1}>
                        <ul
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 pb-10 list-none"
                            aria-label="Other products"
                        >
                            {thirdRowProducts.map((product) => (
                                <ProductCard
                                    key={
                                        product.href ??
                                        product.id ??
                                        product.name
                                            .replace(/\s+/g, '-')
                                            .replace(/[^\w-]/g, '')
                                    }
                                    product={product}
                                    isLarge={false}
                                    center={product.name
                                        .toLowerCase()
                                        .includes('gn solids control')}
                                    variant="pattern"
                                />
                            ))}
                        </ul>
                    </AnimateIn>
                </div>
            </div>
        </section>
    );
};

export default Products;
